import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Select from 'react-select';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import AppConst from '../apis/appconst';

/**
 * IpdNetCollection — Daybook tab showing IPD patients with services grouped by Service Group.
 *
 * Filters: From/To date, Type (Group), User, Search (UHID/name).
 * Per bill: patient details + services grouped by group with subtotals.
 *
 * Props:
 *   - onCountChange(count): optional callback for tab badge.
 */
function IpdNetCollection({ onCountChange }) {
    const today = new Date().toISOString().split('T')[0];

    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);
    const [bills, setBills] = useState([]);
    const [groups, setGroups] = useState([]);
    const [users, setUsers] = useState([]);
    const [totals, setTotals] = useState({ total_bill: 0, total_paid: 0, total_due: 0 });
    const [isFiltered, setIsFiltered] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [selectedGroups, setSelectedGroups] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [expandedBills, setExpandedBills] = useState({}); // bill_id → bool

    // Build dropdown options
    const groupOptions = useMemo(
        () => groups.map(g => ({ value: g.cat_id, label: g.cat_name })),
        [groups]
    );
    const userOptions = useMemo(
        () => users.map(u => ({
            value: u.user_id,
            label: u.fname + (u.lname ? ' ' + u.lname : '')
        })),
        [users]
    );

    // ========== FETCH ==========
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const fd = new FormData();
            fd.append('from_date', fromDate);
            fd.append('to_date', toDate);
            fd.append('group_id', selectedGroups.length === 0 ? 'all' : selectedGroups.map(g => g.value).join(','));
            fd.append('user_id', selectedUsers.length === 0 ? 'all' : selectedUsers.map(u => u.value).join(','));
            fd.append('search', searchKey);

            const resp = await axios.post(
                AppConst.BASE_URL + AppConst.getIpdNetCollectionUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (resp?.data?.status) {
                setBills(resp.data.bills || []);
                setGroups(resp.data.groups || []);
                setUsers(resp.data.users || []);
                setTotals(resp.data.totals || { total_bill: 0, total_paid: 0, total_due: 0 });
                setIsFiltered(!!resp.data.is_filtered);
            } else {
                toast.error(resp?.data?.message || 'Failed to fetch IPD collection');
                setBills([]);
            }
        } catch (e) {
            console.error(e);
            toast.error('Failed to fetch IPD collection');
            setBills([]);
        } finally {
            setIsLoading(false);
        }
    }, [fromDate, toDate, selectedGroups, selectedUsers, searchKey]);

    // Initial load & whenever filters change (search is debounced via button only)
    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromDate, toDate, selectedGroups, selectedUsers]);

    // Notify parent of count
    useEffect(() => {
        if (typeof onCountChange === 'function') onCountChange(bills.length);
    }, [bills.length, onCountChange]);

    // ========== HELPERS ==========
    const formatDate = (d) => {
        if (!d) return 'N/A';
        const dt = new Date(d);
        if (isNaN(dt)) return d;
        return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const formatDateTime = (d) => {
        if (!d) return 'N/A';
        const dt = new Date(d);
        if (isNaN(dt)) return d;
        return dt.toLocaleString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
        });
    };

    const toggleBill = (id) => {
        setExpandedBills(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const expandAll = () => {
        const all = {};
        bills.forEach(b => { all[b.ipd_bill_id] = true; });
        setExpandedBills(all);
    };
    const collapseAll = () => setExpandedBills({});

    const reactSelectStyles = {
        control: (base, state) => ({
            ...base, minHeight: '31px', fontSize: '13px',
            borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
            boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13,110,253,.25)' : 'none',
        }),
        valueContainer: (base) => ({ ...base, padding: '0 6px' }),
        multiValue: (base) => ({ ...base, backgroundColor: '#e7f1ff', borderRadius: '3px', fontSize: '12px' }),
        multiValueLabel: (base) => ({ ...base, color: '#0d6efd', padding: '1px 4px', fontSize: '12px' }),
        placeholder: (base) => ({ ...base, color: '#6c757d', fontSize: '13px' }),
        menu: (base) => ({ ...base, zIndex: 9999, fontSize: '13px' }),
        indicatorsContainer: (base) => ({ ...base, height: '31px' }),
    };

    // ========== PDF ==========
    const handlePrintPDF = () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();

        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('IPD NET COLLECTION', pageWidth / 2, 15, { align: 'center' });

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const dateLine = fromDate === toDate
            ? `Date: ${formatDate(toDate)}`
            : `From: ${formatDate(fromDate)}    To: ${formatDate(toDate)}`;
        doc.text(dateLine, pageWidth / 2, 22, { align: 'center' });

        let y = 30;

        bills.forEach((b, idx) => {
            if (y > 250) { doc.addPage(); y = 15; }

            // Patient header
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text(`${idx + 1}. ${b.patient_name} (${b.uhid})`, 14, y);
            doc.setFont('helvetica', 'normal');
            doc.text(formatDate(b.date), pageWidth - 14, y, { align: 'right' });
            y += 4;

            doc.setFontSize(8);
            doc.text(`Doctor: ${b.doctor || '-'}  |  User: ${b.user_name}`, 14, y);
            y += 4;

            // Services by group
            (b.groups || []).forEach(g => {
                const rows = g.items.map(it => [
                    it.service_name,
                    it.qty,
                    `Rs.${Number(it.charge).toFixed(2)}`,
                    `Rs.${Number(it.total).toFixed(2)}`,
                ]);
                autoTable(doc, {
                    startY: y,
                    head: [[`${g.group_name}`, 'Qty', 'Charge', 'Total']],
                    body: rows,
                    foot: [[
                        { content: 'Subtotal', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold' } },
                        { content: `Rs.${Number(g.subtotal).toFixed(2)}`, styles: { halign: 'right', fontStyle: 'bold' } }
                    ]],
                    theme: 'striped',
                    headStyles: { fillColor: [64, 81, 137], fontSize: 8 },
                    styles: { fontSize: 7, cellPadding: 1.2 },
                    columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' } },
                    margin: { left: 14, right: 14 }
                });
                y = doc.lastAutoTable.finalY + 3;
            });

            // Bill summary box
            autoTable(doc, {
                startY: y,
                body: [[
                    { content: `Total: Rs.${Number(b.total_bill).toFixed(2)}`, styles: { fontStyle: 'bold' } },
                    { content: `Paid: Rs.${Number(b.paid).toFixed(2)}`, styles: { textColor: [0, 128, 0], fontStyle: 'bold' } },
                    { content: `Due: Rs.${Number(b.due).toFixed(2)}`, styles: { textColor: [220, 53, 69], fontStyle: 'bold' } },
                ]],
                theme: 'plain',
                styles: { fontSize: 9 },
                margin: { left: 14, right: 14 }
            });
            y = doc.lastAutoTable.finalY + 6;
        });

        if (y > 260) { doc.addPage(); y = 15; }

        // Grand totals
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(40, 40, 40);
        doc.text(`GRAND TOTAL — Bill: Rs.${totals.total_bill.toFixed(2)}   Paid: Rs.${totals.total_paid.toFixed(2)}   Due: Rs.${totals.total_due.toFixed(2)}`,
            pageWidth / 2, y + 4, { align: 'center' });

        const blob = doc.output('blob');
        window.open(URL.createObjectURL(blob), '_blank');
    };

    // ========== RENDER ==========
    return (
        <div className="ipd-net-collection">
            {/* Top bar */}
            <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-muted">
                    <i className="ri-hospital-line me-1"></i>
                    {fromDate === toDate ? (
                        <>Showing IPD collection for <strong>{formatDate(toDate)}</strong></>
                    ) : (
                        <>From <strong>{formatDate(fromDate)}</strong> to <strong>{formatDate(toDate)}</strong></>
                    )}
                </small>
                <div className="d-flex gap-2">
                    <button className="btn btn-outline-secondary btn-sm" onClick={expandAll} disabled={!bills.length}>
                        <i className="ri-expand-up-down-line me-1"></i>Expand All
                    </button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={collapseAll} disabled={!bills.length}>
                        <i className="ri-contract-up-down-line me-1"></i>Collapse
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={handlePrintPDF} disabled={isLoading || !bills.length}>
                        <i className="ri-printer-line me-1"></i>Print
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="card mb-3">
                <div className="card-body py-2">
                    <div className="row g-2 align-items-end">
                        <div className="col-lg-2 col-md-4 col-6">
                            <label className="form-label small mb-1">From</label>
                            <input type="date" className="form-control form-control-sm"
                                value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                        </div>
                        <div className="col-lg-2 col-md-4 col-6">
                            <label className="form-label small mb-1">To</label>
                            <input type="date" className="form-control form-control-sm"
                                value={toDate} onChange={(e) => setToDate(e.target.value)} />
                        </div>

                        <div className="col-lg-3 col-md-4 col-6">
                            <label className="form-label small mb-1">Type (Group)</label>
                            <Select
                                isMulti
                                options={groupOptions}
                                value={selectedGroups}
                                onChange={(s) => setSelectedGroups(s || [])}
                                placeholder="All Types"
                                isClearable
                                styles={reactSelectStyles}
                                closeMenuOnSelect={false}
                            />
                        </div>

                        <div className="col-lg-3 col-md-4 col-6">
                            <label className="form-label small mb-1">User</label>
                            <Select
                                isMulti
                                options={userOptions}
                                value={selectedUsers}
                                onChange={(s) => setSelectedUsers(s || [])}
                                placeholder="All Users"
                                isClearable
                                isSearchable
                                styles={reactSelectStyles}
                                closeMenuOnSelect={false}
                            />
                        </div>

                        <div className="col-lg-2 col-md-4 col-6">
                            <label className="form-label small mb-1">&nbsp;</label>
                            <button className="btn btn-primary btn-sm w-100" onClick={fetchData} disabled={isLoading}>
                                {isLoading
                                    ? <span className="spinner-border spinner-border-sm"></span>
                                    : <><i className="ri-refresh-line me-1"></i>Refresh</>}
                            </button>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="row mt-2">
                        <div className="col-md-6">
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-light"><i className="ri-search-line"></i></span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="🔍 Search by UHID, Patient Name, Phone..."
                                    value={searchKey}
                                    onChange={(e) => setSearchKey(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') fetchData(); }}
                                />
                                {searchKey && (
                                    <button className="btn btn-outline-secondary" type="button"
                                        onClick={() => { setSearchKey(''); setTimeout(fetchData, 50); }}>
                                        <i className="ri-close-line"></i>
                                    </button>
                                )}
                                <button className="btn btn-primary" type="button" onClick={fetchData}>
                                    Search
                                </button>
                            </div>
                        </div>
                        <div className="col-md-6 d-flex align-items-center">
                            <small className="text-muted">
                                <strong>{bills.length}</strong> patient{bills.length !== 1 ? 's' : ''} found
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            {isFiltered ? (
                <div className="row g-2 mb-3">
                    <div className="col-md-12">
                        <div className="card border-primary h-100">
                            <div className="card-body text-center py-2">
                                <small className="text-muted">
                                    <i className="ri-filter-3-line me-1"></i>
                                    Filtered Total ({selectedGroups.map(g => g.label).join(', ')})
                                </small>
                                <h5 className="text-primary mb-0">₹{totals.total_bill.toFixed(2)}</h5>
                                <small className="text-muted" style={{ fontSize: '11px' }}>
                                    Paid / Due breakdown not shown in filtered view — it's bill-level info.
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="row g-2 mb-3">
                    <div className="col-md-4 col-6">
                        <div className="card border-primary h-100">
                            <div className="card-body text-center py-2">
                                <small className="text-muted"><i className="ri-bill-line me-1"></i>Total Bill</small>
                                <h5 className="text-primary mb-0">₹{totals.total_bill.toFixed(2)}</h5>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4 col-6">
                        <div className="card border-success h-100">
                            <div className="card-body text-center py-2">
                                <small className="text-muted"><i className="ri-check-double-line me-1"></i>Total Paid</small>
                                <h5 className="text-success mb-0">₹{totals.total_paid.toFixed(2)}</h5>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4 col-6">
                        <div className="card border-danger h-100">
                            <div className="card-body text-center py-2">
                                <small className="text-muted"><i className="ri-hand-coin-line me-1"></i>Total Due</small>
                                <h5 className="text-danger mb-0">₹{totals.total_due.toFixed(2)}</h5>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Bills */}
            {isLoading ? (
                <div className="card"><div className="card-body text-center py-5">
                    <div className="spinner-border text-primary"></div>
                    <p className="mt-2 text-muted mb-0">Loading IPD collection...</p>
                </div></div>
            ) : bills.length === 0 ? (
                <div className="card"><div className="card-body text-center py-5">
                    <i className="ri-inbox-line text-muted" style={{ fontSize: '48px' }}></i>
                    <h6 className="mt-2 text-muted">No IPD patients found</h6>
                    <small className="text-muted">
                        Try changing the date range or filters.
                    </small>
                </div></div>
            ) : (
                bills.map((b, idx) => {
                    const isExpanded = expandedBills[b.ipd_bill_id];
                    // Find bed charges and registration groups for quick-glance display
                    const bedGroup = (b.groups || []).find(g => g.is_bed);
                    const regGroup = (b.groups || []).find(g => g.is_registration);
                    const bedTotal = bedGroup ? Number(bedGroup.subtotal) : 0;
                    const regTotal = regGroup ? Number(regGroup.subtotal) : 0;
                    return (
                        <div key={b.ipd_bill_id} className="card mb-3">
                            {/* Patient header */}
                            <div
                                className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2 py-2"
                                style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                                onClick={() => toggleBill(b.ipd_bill_id)}
                            >
                                <div>
                                    <i className={`${isExpanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'} me-1`}></i>
                                    <span className="text-muted small">#{idx + 1}</span>
                                    {' '}
                                    <strong className="text-primary">{b.patient_name}</strong>
                                    <span className="badge bg-light text-dark border ms-2">{b.uhid}</span>
                                    {b.gender && <span className="text-muted ms-2 small">{b.gender}, {b.age || '-'}</span>}
                                    {regTotal > 0 && (
                                        <span className="badge ms-2" style={{ backgroundColor: '#6f42c1', color: 'white' }} title="Registration Charge">
                                            <i className="ri-user-add-line me-1"></i>₹{regTotal.toFixed(2)}
                                        </span>
                                    )}
                                    {bedTotal > 0 && (
                                        <span className="badge bg-info ms-2" title="Bed Charges">
                                            <i className="ri-hotel-bed-line me-1"></i>₹{bedTotal.toFixed(2)}
                                        </span>
                                    )}
                                </div>
                                <div className="d-flex flex-wrap gap-3 align-items-center">
                                    <small className="text-muted">
                                        <i className="ri-calendar-line me-1"></i>{formatDateTime(b.date)}
                                    </small>
                                    <small className="text-muted">
                                        <i className="ri-user-line me-1"></i>{b.user_name}
                                    </small>
                                    {b.is_filtered ? (
                                        <div className="text-end">
                                            <div className="small text-muted">
                                                <i className="ri-filter-3-line me-1"></i>Filtered Total
                                            </div>
                                            <div>
                                                <strong className="text-primary">₹{b.total_bill.toFixed(2)}</strong>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-end">
                                            <div className="small text-muted">Total / Paid / Due</div>
                                            <div>
                                                <strong>₹{b.total_bill.toFixed(2)}</strong>
                                                {' / '}
                                                <span className="text-success">₹{b.paid.toFixed(2)}</span>
                                                {' / '}
                                                <span className="text-danger">₹{b.due.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Expanded: groups of services */}
                            {isExpanded && (
                                <div className="card-body p-3">
                                    {/* Doctor / Phone strip */}
                                    <div className="row g-2 mb-2">
                                        <div className="col-md-4">
                                            <small>
                                                <strong>Doctor:</strong>{' '}
                                                {b.doctor || '-'}
                                            </small>
                                            {b.refer_doctor && b.refer_doctor !== b.doctor && (
                                                <div>
                                                    <small className="text-muted">
                                                        <strong>Referred By:</strong> {b.refer_doctor}
                                                    </small>
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-md-4"><small><strong>Phone:</strong> {b.phone || '-'}</small></div>
                                        <div className="col-md-4"><small><strong>Bill #:</strong> {b.ipd_bill_id}</small></div>
                                    </div>

                                    {(b.groups || []).length === 0 ? (
                                        <p className="text-muted small mb-0">No service items.</p>
                                    ) : (
                                        b.groups.map((g, gi) => {
                                            const isBed = !!g.is_bed;
                                            const isReg = !!g.is_registration;

                                            // Styling per group type
                                            const wrapperStyle = isReg
                                                ? { backgroundColor: '#f3eeff', border: '1px solid #d0bfff' }
                                                : isBed
                                                    ? { backgroundColor: '#e7f5ff', border: '1px solid #a5d8ff' }
                                                    : {};
                                            const wrapperClass = (isBed || isReg) ? 'p-2 rounded' : '';
                                            const titleColor = isReg ? { color: '#6f42c1' } : {};
                                            const titleClass = isReg ? '' : (isBed ? 'text-info' : 'text-primary');
                                            const subtotalClass = isReg ? '' : (isBed ? 'text-info' : 'text-success');
                                            const subtotalStyle = isReg ? { color: '#6f42c1' } : {};
                                            const headerClass = isReg ? '' : (isBed ? 'table-info' : 'table-light');
                                            const headerStyle = isReg ? { backgroundColor: '#e5d9ff' } : {};
                                            const icon = isReg ? 'ri-user-add-line' : (isBed ? 'ri-hotel-bed-line' : 'ri-folder-2-line');
                                            const badgeText = isReg ? 'DEFAULT' : (isBed ? 'DEFAULT' : null);

                                            return (
                                                <div key={gi} className={`mb-3 ${wrapperClass}`} style={wrapperStyle}>
                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                        <strong className={titleClass} style={titleColor}>
                                                            <i className={`${icon} me-1`}></i>
                                                            {g.group_name}
                                                            {badgeText && (
                                                                <span
                                                                    className={`badge ms-2 ${isReg ? '' : 'bg-info'}`}
                                                                    style={{ fontSize: '10px', ...(isReg ? { backgroundColor: '#6f42c1', color: 'white' } : {}) }}
                                                                >
                                                                    {badgeText}
                                                                </span>
                                                            )}
                                                        </strong>
                                                        <strong className={subtotalClass} style={subtotalStyle}>
                                                            ₹{Number(g.subtotal).toFixed(2)}
                                                        </strong>
                                                    </div>
                                                    <div className="table-responsive">
                                                        <table className="table table-sm table-bordered mb-0" style={{ fontSize: '12px' }}>
                                                            <thead className={headerClass} style={headerStyle}>
                                                                <tr>
                                                                    <th style={{ width: '40px' }}>#</th>
                                                                    <th>{isBed ? 'Bed / Ward' : (isReg ? 'Item' : 'Service')}</th>
                                                                    {isBed && <th style={{ width: '150px' }}>Period</th>}
                                                                    <th className="text-end" style={{ width: '70px' }}>{isBed ? 'Days' : 'Qty'}</th>
                                                                    <th className="text-end" style={{ width: '110px' }}>{isBed ? 'Rate/Day' : 'Charge'}</th>
                                                                    <th className="text-end" style={{ width: '90px' }}>Disc</th>
                                                                    <th className="text-end" style={{ width: '110px' }}>Total</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {g.items.map((it, ii) => (
                                                                    <tr key={ii} className={isBed && it.is_old_bed ? 'table-warning' : ''}>
                                                                        <td>{ii + 1}</td>
                                                                        <td>
                                                                            {it.service_name}
                                                                            {isBed && it.is_old_bed && (
                                                                                <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '9px' }}>
                                                                                    <i className="ri-arrow-left-right-line me-1"></i>OLD / TRANSFERRED
                                                                                </span>
                                                                            )}
                                                                            {isBed && !it.is_old_bed && it.is_bed && (
                                                                                <span className="badge bg-success ms-2" style={{ fontSize: '9px' }}>
                                                                                    CURRENT
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                        {isBed && (
                                                                            <td className="small text-muted">
                                                                                {it.bed_start_date ? formatDate(it.bed_start_date) : '-'}
                                                                                {it.bed_transfer_date && (<> → {formatDate(it.bed_transfer_date)}</>)}
                                                                            </td>
                                                                        )}
                                                                        <td className="text-end">{it.qty}</td>
                                                                        <td className="text-end">₹{Number(it.charge).toFixed(2)}</td>
                                                                        <td className="text-end">{Number(it.discount) > 0 ? `₹${Number(it.discount).toFixed(2)}` : '-'}</td>
                                                                        <td className="text-end fw-bold">₹{Number(it.total).toFixed(2)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                            <tfoot className={headerClass} style={headerStyle}>
                                                                <tr>
                                                                    <td colSpan={isBed ? '6' : '5'} className="text-end fw-bold">Subtotal:</td>
                                                                    <td className={`text-end fw-bold ${subtotalClass}`} style={subtotalStyle}>
                                                                        ₹{Number(g.subtotal).toFixed(2)}
                                                                    </td>
                                                                </tr>
                                                            </tfoot>
                                                        </table>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })
            )}

            {/* Grand total footer */}
            {bills.length > 0 && (
                <div className="card bg-dark text-white">
                    <div className="card-body py-3">
                        {isFiltered ? (
                            <div className="text-center">
                                <small className="text-white-50">
                                    <i className="ri-filter-3-line me-1"></i>
                                    Filtered Grand Total ({selectedGroups.map(g => g.label).join(', ')})
                                </small>
                                <h4 className="mb-0 text-warning">₹{totals.total_bill.toFixed(2)}</h4>
                            </div>
                        ) : (
                            <div className="row text-center">
                                <div className="col-md-4">
                                    <small className="text-white-50">Total Bill</small>
                                    <h5 className="mb-0 text-white">₹{totals.total_bill.toFixed(2)}</h5>
                                </div>
                                <div className="col-md-4">
                                    <small className="text-white-50">Total Paid</small>
                                    <h5 className="mb-0 text-success">₹{totals.total_paid.toFixed(2)}</h5>
                                </div>
                                <div className="col-md-4">
                                    <small className="text-white-50">Total Due</small>
                                    <h5 className="mb-0 text-warning">₹{totals.total_due.toFixed(2)}</h5>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default IpdNetCollection;